import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import API_URL from '../config/api';

interface ApiError {
  success: false;
  error: string;
}

interface CsrfTokenResponse {
  success: true;
  data: {
    csrfToken: string;
  };
}

class ApiClient {
  private client: AxiosInstance;
  private pendingRequests = new Map<string, Promise<any>>();
  private csrfToken: string | null = null;
  private csrfTokenPromise: Promise<string | null> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Request interceptor to add auth token and CSRF token
    this.client.interceptors.request.use(
      async (config) => {
        // Skip auth header for CSRF token endpoint (it doesn't need auth
        // and sending an expired token causes a 401 cascade)
        const isCsrfRequest = config.url === '/csrf-token';

        // Add auth token (skip for CSRF endpoint)
        if (!isCsrfRequest) {
          const token = localStorage.getItem('auth_token');
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        // Add CSRF token for state-changing requests
        if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(config.method?.toUpperCase() || '')) {
          const csrfToken = await this.getCsrfToken();
          if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        // Handle CSRF token errors (403)
        if (error.response?.status === 403 && error.response?.data?.error?.includes('CSRF')) {
          // Clear current token and fetch new one
          this.csrfToken = null;
          this.csrfTokenPromise = null;
          
          // Retry the request with new token
          const csrfToken = await this.getCsrfToken();
          if (csrfToken && error.config) {
            error.config.headers = error.config.headers || {};
            error.config.headers['X-CSRF-Token'] = csrfToken;
            return this.client.request(error.config);
          }
        }

        if (error.response?.status === 401) {
          // Don't handle 401 errors on login/register pages or CSRF requests
          const isAuthPage = window.location.pathname === '/login' || window.location.pathname === '/register';
          const isCsrfRequest = error.config?.url === '/csrf-token';
          if (!isAuthPage && !isCsrfRequest) {
            // Clear token and redirect to login for session expiration
            localStorage.removeItem('auth_token');
            localStorage.removeItem('current_user');
            this.csrfToken = null;
            this.csrfTokenPromise = null;
            toast.error('Session expired. Please log in again.');
            window.location.href = '/login';
          }
          // For auth pages, let the error propagate so the login/register form can handle it
        } else if (error.response?.status && error.response.status >= 500) {
          toast.error('Server error. Please try again later.');
        } else if (!error.response?.status || error.response.status >= 500) {
          // Only show toast for network errors and 5xx — let components handle 4xx inline
          if (error.message && !error.response?.status) {
            toast.error(error.message);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Get CSRF token from server
   * Tokens are cached and reused until they expire
   */
  private async getCsrfToken(): Promise<string | null> {
    // Return cached token if available
    if (this.csrfToken) {
      return this.csrfToken;
    }

    // Return existing promise if already fetching
    if (this.csrfTokenPromise) {
      return this.csrfTokenPromise;
    }

    // Fetch new token
    this.csrfTokenPromise = this.client
      .get<CsrfTokenResponse>('/csrf-token')
      .then((response): string | null => {
        if (response.data.success && response.data.data.csrfToken) {
          this.csrfToken = response.data.data.csrfToken;
          return this.csrfToken;
        }
        return null;
      })
      .catch((error) => {
        console.error('Failed to fetch CSRF token:', error);
        return null;
      })
      .finally(() => {
        this.csrfTokenPromise = null;
      });

    return this.csrfTokenPromise;
  }

  /**
   * Clear CSRF token (e.g., on logout)
   */
  clearCsrfToken(): void {
    this.csrfToken = null;
    this.csrfTokenPromise = null;
  }

  // Deduplicate concurrent requests
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const requestKey = `GET:${url}:${JSON.stringify(config?.params || {})}`;
    
    if (this.pendingRequests.has(requestKey)) {
      return this.pendingRequests.get(requestKey)!;
    }

    const promise = this.client.get<T>(url, config)
      .then(response => response.data)
      .finally(() => {
        this.pendingRequests.delete(requestKey);
      });

    this.pendingRequests.set(requestKey, promise);
    return promise;
  }

  getClient(): AxiosInstance {
    return this.client;
  }

  setAuthToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  clearAuthToken(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('current_user');
    this.clearCsrfToken();
  }
}

export const apiClient = new ApiClient();
export default apiClient.getClient();
